import { useMemo } from "react";
import {
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
  type PieSectorShapeProps,
} from "recharts";
import {
  Card,
  CardContent,
  CardFooter,
  Chip,
  Division,
  Flex,
  Heading,
  Icon,
  Icons,
  Paragraph,
  Skeleton,
  Span,
} from "@ir/ui";
import { useAnalyticsKPI, useAnalyticsMobile } from "@/hooks";
import { ScoreCardTrend } from "@/core/repositories/analytics";
import {
  ANALYTICS_CHECK_DATE_RANGE_LABEL,
  groupRetailersWithOther,
  RetailerChartItem,
} from "@/store/analytics";
import EmptyKpiCard from "./EmptyKpiCard";
import "../../styles/Analytics.page.scss";

const CHART_COLORS = [
  "var(--primary-400)",
  "var(--secondary-400)",
  "var(--warning-400)",
  "var(--error-400)",
  "var(--primary-300)",
  "var(--success-500)",
  "var(--secondary-600)",
  "var(--success-300)",
  "var(--grey-100)",
];

const HEADING = "Deductions by Retailer";

const RETAILER_SKELETON_ROW_KEYS = ["row-1", "row-2", "row-3", "row-4", "row-5", "row-6", "row-7", "row-8", "row-9", "row-10"] as const;

const RETAILER_SKELETON_NAME_WIDTHS: Record<(typeof RETAILER_SKELETON_ROW_KEYS)[number], number> = {
  "row-1": 48,
  "row-2": 32,
  "row-3": 36,
  "row-4": 40,
  "row-5": 28,
  "row-6": 44,
  "row-7": 32,
  "row-8": 36,
  "row-9": 40,
  "row-10": 48,
};

type RetailerPieSlice = RetailerChartItem & { fill: string };

const renderRetailerPieSector = (props: PieSectorShapeProps) => <Sector {...props} />;

const formatCurrencyFull = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const TREND_ARROWS: Record<ScoreCardTrend, string> = {
  UP: "↑",
  DOWN: "↓",
  NEUTRAL: "→",
};

const formatPopIndicator = (vsPrior: number | null, trend: ScoreCardTrend | null): string | null => {
  if (vsPrior == null || trend == null) return null;

  return `${TREND_ARROWS[trend]}${Math.abs(Math.round(vsPrior))}%`;
};

const getVsPriorColor = (trend: ScoreCardTrend | null) => {
  if (trend === "UP") return "text-error-700";
  if (trend === "DOWN") return "text-success-700";
  return "text-grey-400";
};

const VsPriorIndicator = ({ vsPrior, trend }: { vsPrior: number | null; trend: ScoreCardTrend | null }) => {
  if (vsPrior === null || trend === null) {
    return (
      <Span className="d-inline-flex text-grey-400 text-sm">
        <Icon name={Icons.Minus} size="small" />
      </Span>
    );
  }

  return (
    <Span className={`d-inline-flex flex-align-center text-sm ${getVsPriorColor(trend)}`}>
      {trend === "UP" && <Icon name={Icons.ArrowNarrowUp} size="small" />}
      {trend === "DOWN" && <Icon name={Icons.ArrowNarrowDown} size="small" />}
      {trend === "NEUTRAL" && <Icon name={Icons.ArrowRight} size="small" />}
      <Span>{Math.abs(Math.round(vsPrior))}%</Span>
    </Span>
  );
};

const mapRetailersToPieSlices = (retailers: RetailerChartItem[]): RetailerPieSlice[] => {
  let topIndex = 0;

  return retailers.map(item => ({
    ...item,
    fill: item.isOther ? CHART_COLORS.at(-1)! : CHART_COLORS[topIndex++ % (CHART_COLORS.length - 1)],
  }));
};

const RetailerPieTooltip = ({ active, payload }: { active?: boolean; payload?: { payload: RetailerPieSlice }[] }) => {
  if (!active || !payload?.length) return null;

  const item = payload[0].payload;
  const popLabel = formatPopIndicator(item.vsPrior, item.trend);

  return (
    <Division
      style={{
        background: "var(--grey-900)",
        borderRadius: 8,
        padding: "10px 14px",
        color: "var(--white-color)",
        fontSize: 13,
        minWidth: 180,
      }}
    >
      <Flex align="center" gap={0.5} style={{ fontWeight: 600, marginBottom: 6 }}>
        <Span
          className="d-inline-block"
          style={{ height: 12, width: 12, borderRadius: 2, backgroundColor: item.fill }}
        />
        {item.retailerName}
      </Flex>
      <Division style={{ marginBottom: 4 }}>{formatCurrencyFull(item.amount)}</Division>
      <Division style={{ marginBottom: popLabel ? 4 : 0, color: "var(--grey-300)" }}>
        {item.share === 100 ? 100 : item.share.toFixed(2)}% share
      </Division>
      {popLabel && <Division>{popLabel}</Division>}
    </Division>
  );
};

const RetailerLegendTableHeader = () => (
  <thead>
    <tr>
      <th className="pb-2" style={{ width: 28 }} />
      <th className="pb-2" />
      <th style={{ width: 100 }} className="text-right pb-2">
        <Span className="text-grey-400 font-bold text-sm">Amount</Span>
      </th>
      <th style={{ width: 100 }} className="text-right pb-2">
        <Span className="text-grey-400 font-bold text-sm">Share</Span>
      </th>
      <th style={{ width: 100 }} className="text-right pb-2">
        <Span className="text-grey-400 font-bold text-sm">vs Prior</Span>
      </th>
    </tr>
  </thead>
);

const RetailerLegendTableHeaderSkeleton = () => (
  <thead>
    <tr>
      <th />
      <th />
      <th className="text-right">
        <Flex justifyContent="end">
          <Skeleton width={56} height={12} style={{ borderRadius: 4 }} />
        </Flex>
      </th>
      <th className="text-right">
        <Flex justifyContent="end">
          <Skeleton width={40} height={12} style={{ borderRadius: 4 }} />
        </Flex>
      </th>
      <th className="text-right">
        <Flex justifyContent="end">
          <Skeleton width={40} height={12} style={{ borderRadius: 4 }} />
        </Flex>
      </th>
    </tr>
  </thead>
);

const RetailerLegendRow = ({ item }: { item: RetailerPieSlice }) => (
  <tr>
    <td valign="middle">
      <Span
        className="d-block m-auto"
        style={{ height: 12, width: 12, borderRadius: 2, backgroundColor: item.fill }}
      />
    </td>
    <td valign="middle" style={{ whiteSpace: "nowrap" }}>
      <Span className="retailer-legend-name text-grey-700 font-bold text-sm">{item.retailerName}</Span>
    </td>
    <td className="text-right" valign="middle">
      <Span className="text-grey-600 font-bold text-sm">{formatCurrencyFull(item.amount)}</Span>
    </td>
    <td className="text-right" valign="middle">
      <Span className="text-grey-500 font-medium text-sm">{item.share === 100 ? 100 : item.share.toFixed(2)}%</Span>
    </td>
    <td className="text-right" valign="middle">
      <VsPriorIndicator vsPrior={item.vsPrior} trend={item.trend} />
    </td>
  </tr>
);

const RetailerLegend = ({ items }: { items: RetailerPieSlice[] }) => (
  <Division className="retailer-legend-container">
    <table className="retailer-legend-table w-100">
      <RetailerLegendTableHeader />
      <tbody>
        {items.map(item => (
          <RetailerLegendRow key={item.retailerName} item={item} />
        ))}
      </tbody>
    </table>
  </Division>
);

const RetailerLegendRowSkeleton = ({ rowKey }: { rowKey: (typeof RETAILER_SKELETON_ROW_KEYS)[number] }) => (
  <tr>
    <td>
      <Skeleton width={12} height={12} style={{ borderRadius: 2 }} />
    </td>
    <td>
      <Skeleton width={RETAILER_SKELETON_NAME_WIDTHS[rowKey]} height={12} style={{ borderRadius: 4 }} />
    </td>
    <td className="text-right">
      <Flex justifyContent="end">
        <Skeleton width={56} height={12} style={{ borderRadius: 4 }} />
      </Flex>
    </td>
    <td className="text-right">
      <Flex justifyContent="end">
        <Skeleton width={40} height={12} style={{ borderRadius: 4 }} />
      </Flex>
    </td>
    <td className="text-center">
      <Flex justifyContent="end">
        <Skeleton width={40} height={12} style={{ borderRadius: 4 }} />
      </Flex>
    </td>
  </tr>
);

const RetailerPieSkeleton = () => (
  <Flex
    direction="row"
    gap={1}
    wrap="wrap"
    style={{ minHeight: 320, alignItems: "center" }}
  >
    <Division className="flex-1" style={{ minWidth: 180, maxWidth: 300 }}>
      <Flex align="center" justifyContent="center" style={{ height: 280 }}>
        <Skeleton width={220} height={220} style={{ borderRadius: "50%" }} />
      </Flex>
    </Division>
    <Division className="flex-1" style={{ minWidth: 200, marginRight: 24 }}>
      <table className="retailer-legend-table" style={{ width: "100%", borderCollapse: "collapse" }}>
        <RetailerLegendTableHeaderSkeleton />
        <tbody>
          {RETAILER_SKELETON_ROW_KEYS.map(rowKey => (
            <RetailerLegendRowSkeleton key={rowKey} rowKey={rowKey} />
          ))}
        </tbody>
      </table>
    </Division>
  </Flex>
);

const RetailerPieChart = ({ items }: { items: RetailerPieSlice[] }) => (
  <Division style={{ position: "relative", width: "100%", height: 280 }}>
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={items}
          dataKey="amount"
          nameKey="retailerName"
          cx="50%"
          cy="50%"
          outerRadius="82%"
          paddingAngle={0}
          stroke="none"
          isAnimationActive={false}
          shape={renderRetailerPieSector}
          labelLine={false}
        />
        <Tooltip wrapperStyle={{ zIndex: 2 }} content={<RetailerPieTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  </Division>
);

const RetailerPieContent = ({ items }: { items: RetailerPieSlice[] }) => (
  <Flex direction="row" gap={0.25} className="analytics-pie-composite" style={{ minHeight: 320 }} align="center" wrap="wrap">
    <Division className="flex-1" style={{ minWidth: 200, maxWidth: 320 }}>
      <RetailerPieChart items={items} />
    </Division>
    <Division className="flex-1" style={{ minWidth: 200 }}>
      <RetailerLegend items={items} />
    </Division>
  </Flex>
);

const DeductionsByRetailerCard = () => {
  const { deductionsByRetailer } = useAnalyticsKPI();
  const isMobile = useAnalyticsMobile();
  const pieSlices = useMemo(() => {
    if (!deductionsByRetailer.data?.retailers.length) return [];
    const grouped = groupRetailersWithOther(deductionsByRetailer.data.retailers);
    return mapRetailersToPieSlices(grouped);
  }, [deductionsByRetailer.data]);

  const cardContent = (() => {
    if (deductionsByRetailer.isLoading) {
      return <RetailerPieSkeleton />;
    }

    if (pieSlices.length === 0) {
      return (
        <Flex justifyContent="center" align="center" className="empty-kpi-card-container" style={{ height: 350 }}>
          <EmptyKpiCard
            title="No data available"
            description="No deductions recorded for the selected period. Try adjusting the date range."
            icon={Icons.BarChart5}
          />
        </Flex>
      );
    }

    return <RetailerPieContent items={pieSlices} />;
  })();

  return (
    <Card className="bg-white border-1 border-color-grey-50">
      <Flex className="px-4 py-3 border-bottom-1 border-color-grey-25" direction="column">
        <Heading text={HEADING} level={6} className="m-0 text-grey-500 font-bold" />
        {!isMobile && (
          <Paragraph className="m-0 text-grey-400">Deduction spend by retailer account</Paragraph>
        )}
      </Flex>
      <CardContent style={{ minHeight: 370 }}>{cardContent}</CardContent>
      <CardFooter
        className="analytics-card-footer bg-grey-10 text-grey-500"
        style={{ borderBottomLeftRadius: ".75rem", borderBottomRightRadius: ".75rem" }}
      >
        <Flex gap={0.5} align="center" wrap="wrap">
          Filter applied:{" "}
          <Chip label={ANALYTICS_CHECK_DATE_RANGE_LABEL} variant="filled" color="secondary" borderless />
          <Chip label="Purchaser" variant="filled" color="secondary" borderless />
        </Flex>
      </CardFooter>
    </Card>
  );
};

export default DeductionsByRetailerCard;
